import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';

import { supabase } from '../lib/supabaseClient';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const stringifySafe = (value: unknown) => {
  try {
    return JSON.stringify(value ?? null);
  } catch {
    return '';
  }
};

// Supabase re-emits SIGNED_IN with a freshly parsed session object every time the
// tab becomes visible again. Comparing the meaningful fields lets us keep the
// previous object identity so consumers (and their effects) don't re-run.
const isSameUser = (a: User | null, b: User | null) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id &&
    a.email === b.email &&
    a.phone === b.phone &&
    a.updated_at === b.updated_at &&
    stringifySafe(a.app_metadata) === stringifySafe(b.app_metadata) &&
    stringifySafe(a.user_metadata) === stringifySafe(b.user_metadata)
  );
};

const isSameSession = (a: Session | null, b: Session | null) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.access_token === b.access_token &&
    a.refresh_token === b.refresh_token &&
    a.expires_at === b.expires_at &&
    isSameUser(a.user ?? null, b.user ?? null)
  );
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applyAuthState = useCallback((nextSession: Session | null) => {
    setSession((prev) => (isSameSession(prev, nextSession) ? prev : nextSession));
    setUser((prev) => {
      const nextUser = nextSession?.user ?? null;
      return isSameUser(prev, nextUser) ? prev : nextUser;
    });
  }, []);

  useEffect(() => {
    let isMounted = true;

    supabase.auth
      .getSession()
      .then(({ data, error: sessionError }) => {
        if (!isMounted) return;
        if (sessionError) {
          setError(sessionError.message);
        }
        applyAuthState(data.session);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    const subscription = supabase.auth.onAuthStateChange((_event, newSession) => {
      applyAuthState(newSession);
    });

    return () => {
      isMounted = false;
      subscription.data.subscription.unsubscribe();
    };
  }, [applyAuthState]);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      setError(signInError.message);
      throw signInError;
    }
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    const configuredSiteUrl =
      import.meta.env.VITE_PUBLIC_SITE_URL ??
      import.meta.env.VITE_SITE_URL ??
      import.meta.env.VITE_SUPABASE_SITE_URL ??
      null;

    const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;
    const redirectBase = configuredSiteUrl || fallbackOrigin;

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectBase ? `${redirectBase.replace(/\/$/, '')}/reset-password` : undefined,
    });

    if (resetError) {
      throw resetError;
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    const attempt = await supabase.auth.signOut({ scope: 'local' });
    setSession(null);
    setUser(null);

    if (attempt.error) {
      const message = attempt.error.message || '';
      if (attempt.error.status === 403 || /Auth session missing/i.test(message)) {
        return;
      }
      setError(attempt.error.message);
      throw attempt.error;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const { data, error: userError } = await supabase.auth.getUser();
    if (userError) {
      setError(userError.message);
      throw userError;
    }
    setUser((prev) => {
      const nextUser = data.user ?? null;
      return isSameUser(prev, nextUser) ? prev : nextUser;
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      loading,
      error,
      signIn,
      requestPasswordReset,
      signOut,
      refreshUser,
    }),
    [session, user, loading, error, signIn, requestPasswordReset, signOut, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
